import type { YMarketOrder } from '@/lib/types'
import { FieldRow } from '@/components/ui/FieldRow'
import { EM_DASH, paymentTotal } from '@/lib/order-helpers'
import { LocalDateTime } from '@/components/ui/LocalDateTime'
import { ProductThumbnail } from '@/components/orders/ProductThumbnail'
import { orderLinePiece, PieceReference } from '@/components/piece/PieceReference'
import { OrderItemFinancials } from '@/components/orders/OrderItemFinancials'

function formatYandexDate(dateStr?: string): string {
  if (!dateStr) return EM_DASH
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return dateStr
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
}

export function FbsDetail({ order }: { order: YMarketOrder }) {
  const shipmentDate = order.delivery?.shipment?.shipmentDate
  const shipmentId = order.delivery?.shipment?.id
  const box = order.delivery?.boxesLayout?.[0]
  const cityHint = order.delivery?.serviceName || 'Доставка Яндекса'
  const total = paymentTotal(order)
  const singleItemPrices = order.items?.length === 1 ? order.prices : undefined

  return (
    <>
      <div className="surface-card rounded-md mb-6 md:mb-8 px-4 sm:px-6 py-1">
        <FieldRow label="Заказ оформлен" value={<LocalDateTime value={order.creationDate} />} />
        <FieldRow label="Отгрузка на СЦ" value={formatYandexDate(shipmentDate)} accent />
        {shipmentId != null && <FieldRow label="ID отгрузки" value={String(shipmentId)} />}
        {box?.barcode && <FieldRow label="Штрихкод коробки" value={box.barcode} />}
        {box?.boxId != null && <FieldRow label="ID коробки" value={String(box.boxId)} />}
        <FieldRow label="Доставка" value={cityHint} />
        <FieldRow
          label="Оплата"
          value={
            order.paymentType === 'PREPAID' ? 'Предоплата'
            : order.paymentType === 'POSTPAID' ? 'При получении'
            : order.paymentType || EM_DASH
          }
        />
        {total != null && (
          <FieldRow label="Сумма" value={`${total.toLocaleString('ru-RU')} ₽`} bold />
        )}

        {order.items && order.items.length > 0 && (
          <div className="py-3.5 border-b border-rule last:border-0">
            <div className="text-[11px] tracking-[0.08em] uppercase text-ink-muted font-semibold mb-2.5">
              Товары
            </div>
            <div className="space-y-2">
              {order.items.map((item, i) => {
                const product = item.offerName || item.offerId || 'Товар'
                return (
                  <div key={i} className="text-[14px]">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <ProductThumbnail item={item} label={`Фото: ${product}`} className="size-9" />
                      <span className="flex min-w-0 flex-1 truncate text-ink">
                        <PieceReference piece={orderLinePiece(item, product)} originLabel={`заказу №${order.orderId}`} />
                      </span>
                      <span className="text-ink-faint text-[12px] tab-nums">×{item.count || 1}</span>
                    </div>
                    <OrderItemFinancials item={item} fallbackPrices={singleItemPrices} className="ml-11 sm:ml-12" />
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>

      <div className="surface-card rounded-md p-5 mb-8 text-[13px] text-ink-muted leading-[1.6]">
        Яндекс везёт сам. Сообщения покупателю и трек СДЭКа здесь не нужны —
        {box?.barcode
          ? ' упакуй товар в коробку со штрихкодом выше и сдай на сортировочный центр к указанной дате.'
          : ' упакуй товар, наклей ярлык Маркета (кнопка печати на карточке заказа) и сдай на сортировочный центр к указанной дате.'}
      </div>
    </>
  )
}
